"use client";

import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import type { ChatEmailSummary, ChatSummary } from "@/types/chat";
import { AlertType } from "@/types/enums/alert-type";
import { ChatStatus } from "@/types/enums/chat";
import { EmailDirection } from "@/types/enums/email";
import { chatQueryKeys, prependNewChatToInfiniteLists, updateChatOptimistically, updateOrAddEmail } from "@/query/comms/chat";

// Alert types that should trigger email cache updates
const EMAIL_ALERT_TYPES = [AlertType.EMAIL_RECEIVED, AlertType.EMAIL_REPLY_RECEIVED, AlertType.EMAIL_SENT, AlertType.EMAIL_INVOICE_SENT];

// Inbound email alert types that should mark chat as UNREAD and increment unread count
const INBOUND_EMAIL_ALERT_TYPES = new Set([AlertType.EMAIL_RECEIVED, AlertType.EMAIL_REPLY_RECEIVED]);

// Type for email alert data
interface EmailAlertData {
  email: ChatEmailSummary;
  chat: ChatSummary | null;
  patientIds: string[];
}

// Type guard for EmailAlertData
const isEmailAlertData = (data: unknown): data is EmailAlertData => {
  return typeof data === "object" && data !== null && "email" in data && typeof (data as EmailAlertData).email === "object" && (data as EmailAlertData).email !== null && "id" in (data as EmailAlertData).email;
};

/**
 * Hook that subscribes to email-related websocket alerts and updates React Query cache.
 * This ensures that all components using React Query for chat data automatically
 * receive real-time email updates.
 *
 * Separate from useChatRealtimeCache to maintain single responsibility.
 */
export function useEmailRealtimeCache() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleMessage(alert) {
      const data = alert.data;
      if (!isEmailAlertData(data)) {
        return;
      }
      const {
        email,
        chat
      } = data;
      if (!chat) {
        return;
      }
      const isInboundMessage = email.direction === EmailDirection.INBOUND;
      prependNewChatToInfiniteLists(chat, undefined, {
        isInboundMessage
      });
      updateChatOptimistically(chat.id, chatDetail => ({
        ...chatDetail,
        emails: updateOrAddEmail(chatDetail.emails, email),
        status: INBOUND_EMAIL_ALERT_TYPES.has(alert.type) ? ChatStatus.UNREAD : chatDetail.status
      }), chatSummary => ({
        ...chatSummary,
        latestMessageContent: chat.latestMessageContent,
        latestMessageDate: chat.latestMessageDate,
        status: INBOUND_EMAIL_ALERT_TYPES.has(alert.type) ? ChatStatus.UNREAD : chatSummary.status
      }));
      if (INBOUND_EMAIL_ALERT_TYPES.has(alert.type)) {
        queryClient.setQueryData(chatQueryKeys.unreadCount(), _temp);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleMessage = t0;
  useWebSocketCallbackSubscription(EMAIL_ALERT_TYPES, handleMessage, true, "useEmailRealtimeCache");
}
function _temp(old) {
  return (old ?? 0) + 1;
}